import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { createProgrammingLesson } from '../../services/api';
import ErrorCard from '../../components/ErrorCard/ErrorCard';
import './ProgrammingLessonCreator.css';

const ProgrammingLessonCreator = () => {
  const navigate = useNavigate();
  const { handleRequest, loading } = useApi();
  const [errorMessage, setErrorMessage] = useState('');
  const [lessonData, setLessonData] = useState({
    title: '',
    description: '',
    language: 'html',
    level: 'basic',
    content: '',
    taskCount: 3,
    lessonLanguage: 'en'
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLessonData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!lessonData.title.trim()) {
      setErrorMessage('Vennligst oppgi en leksjonstittel');
      return;
    }

    if (!lessonData.content.trim()) {
      setErrorMessage('Vennligst legg inn leksjonsinnhold slik at AI kan generere oppgaver');
      return;
    }

    try {
      const formattedData = {
        title: lessonData.title,
        description: lessonData.description,
        language: lessonData.language,
        level: lessonData.level,
        content: lessonData.content,
        taskCount: parseInt(lessonData.taskCount) || 3,
        lessonLanguage: lessonData.lessonLanguage
      };

      await handleRequest(createProgrammingLesson, formattedData);
      navigate('/dashboard');
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke opprette programmeringsleksjon');
    }
  };

  return (
    <div className="programming-lesson-creator-container">
      <header className="creator-header">
        <div className="header-content">
          <h1>Opprett programmeringsleksjon</h1>
          <button 
            className="back-button" 
            onClick={() => navigate('/dashboard')}
          >
            Tilbake til oversikten
          </button>
        </div>
      </header>
      <main className="creator-content">
        <div className="creator-card">
          <div className="creator-intro">
            <h2>AI-basert leksjonsoppretting</h2>
            <p>Legg inn leksjonsinnholdet ditt, så kan AI automatisk generere oppgaver, instruksjoner, startkode og forventet oppførsel!</p>
          </div>

          {errorMessage && (
            <ErrorCard 
              message={errorMessage} 
              onClose={() => setErrorMessage('')}
            />
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="title">Leksjonstittel *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={lessonData.title}
                onChange={handleChange}
                placeholder="f.eks. Introduksjon til HTML-skjemaer"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Beskrivelse (valgfritt)</label>
              <textarea
                id="description"
                name="description"
                value={lessonData.description}
                onChange={handleChange}
                placeholder="Kort beskrivelse av leksjonen"
                rows="2"
              />
            </div>

            <div className="form-row">
              <div className="form-group third-width">
                <label htmlFor="language">Programmeringsspråk *</label>
                <select
                  id="language"
                  name="language"
                  value={lessonData.language}
                  onChange={handleChange}
                  required
                >
                  <option value="html">HTML</option>
                  <option value="css">CSS</option>
                  <option value="javascript">JavaScript</option>
                  <option value="mixed">Blandet (HTML/CSS/JS)</option>
                </select>
              </div>

              <div className="form-group third-width">
                <label htmlFor="level">Vanskelighetsgrad *</label>
                <select
                  id="level"
                  name="level"
                  value={lessonData.level}
                  onChange={handleChange}
                  required
                >
                  <option value="basic">Grunnleggende</option>
                  <option value="intermediate">Middels</option>
                  <option value="advanced">Avansert</option>
                </select>
              </div>

              <div className="form-group third-width">
                <label htmlFor="lessonLanguage">Språk (innhold) *</label>
                <select
                  id="lessonLanguage"
                  name="lessonLanguage"
                  value={lessonData.lessonLanguage}
                  onChange={handleChange}
                  required
                >
                  <option value="en">Engelsk</option>
                  <option value="no">Norsk</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="content">Leksjonsinnhold *</label>
              <p className="field-hint">Beskriv hva elevene skal lære. AI vil generere oppgaver, instruksjoner, startkode og forventet oppførsel basert på dette.</p>
              <textarea
                id="content"
                name="content"
                value={lessonData.content}
                onChange={handleChange}
                placeholder="f.eks. I denne leksjonen skal elevene lære om HTML-skjemaer. De bør forstå hvordan man lager input-felt, tekstområder, knapper og innsending av skjema. Temaer inkluderer: skjemaelementer, input-typer, etiketter og validering."
                rows="8"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="taskCount">Antall oppgaver</label>
              <input
                type="number"
                id="taskCount"
                name="taskCount"
                value={lessonData.taskCount}
                onChange={handleChange}
                min="1"
                max="10"
                placeholder="3"
              />
              <p className="field-hint">Hvor mange kodeoppgaver skal AI generere? (1–10, standard: 3)</p>
            </div>

            <div className="form-actions">
              <button 
                type="submit" 
                className="create-lesson-btn"
                disabled={loading}
              >
                {loading ? 'Genererer oppgaver med AI...' : '✨ Generer leksjon med AI'}
              </button>
              <button 
                type="button" 
                className="cancel-btn"
                onClick={() => navigate('/dashboard')}
              >
                Avbryt
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default ProgrammingLessonCreator;
